/**
 * yt-quick-add.js — add a base blend to the basket straight from a category
 * page (Lucas, 28 Sep 2026: "from each of the category pages they can add to
 * basket straight from there").
 *
 * Markup:  <button data-yt-quick-add="<base slug>" data-yt-tin="<tin name>">
 *          inside an element with [data-yt-quick-add-scope] that holds a
 *          [data-yt-quick-add-error] line.
 *
 * Same machinery as the base product page (yt-base-landing.js addToCart),
 * with no add-ons and the 30-day plan:
 *   POST /formula/submit { base_slug, ingredients: [] }
 *   GET  /formula/:id/pdp  → the registered bundle lines (base, spoon)
 *   POST /cart/add.js      → lines on the 30-day selling plan
 * then the site's basket drawer opens (window.__ytCartDrawer), or /cart.
 */
(function () {
  'use strict';
  if (window.__ytQuickAdd) return;
  window.__ytQuickAdd = true;

  var PROXY = '/apps/yutrients';
  // 21 Oct 2026: under the one-time 30-day test a quick-add is a one-time
  // line stamped `_yt_offer: ot30` (see yt-base-landing.js), not a 30-day plan.
  function otMode() { return window.__ytOfferMode === 'one_time_30d'; }

  function uuidv4() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  async function readError(res, fallback) {
    try {
      var j = await res.json();
      if (j && j.error && typeof j.error === 'object') {
        return (j.error.details && j.error.details.message_for_human) || j.error.message || fallback;
      }
      return (j && (j.message || j.description)) || fallback;
    } catch (_e) {
      return fallback;
    }
  }

  async function add(btn) {
    if (btn.__busy) return;
    btn.__busy = true;
    var scope = btn.closest('[data-yt-quick-add-scope]') || btn.parentNode;
    var errEl = scope && scope.querySelector('[data-yt-quick-add-error]');
    if (errEl) errEl.hidden = true;
    var slug = btn.getAttribute('data-yt-quick-add');
    var tin = (btn.getAttribute('data-yt-tin') || '').slice(0, 60);
    var original = btn.innerHTML;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Adding…';
    try {
      // 10 Oct 2026: a second press (or a return to the page) must not stack a
      // second copy of the same base — a real basket on 10 Oct carried Meno
      // Sleep twice at £79.90. If the basket already holds this base on the
      // 30-day plan, skip the add and just carry on to checkout / the drawer.
      var already = false;
      try {
        var cartNow = await (await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })).json();
        already = (cartNow.items || []).some(function (i) {
          if (i.handle !== 'base-' + slug) return false;
          return otMode() ? !!(i.properties && i.properties._yt_offer === 'ot30') : !!i.selling_plan_allocation;
        });
      } catch (_e) {}
      if (already) {
        btn.textContent = 'In your basket ✓';
        if (btn.dataset.ytAfter === 'checkout') { window.location.href = '/checkout'; return; }
        if (window.__ytCartDrawer && typeof window.__ytCartDrawer.open === 'function') { await window.__ytCartDrawer.open(); }
        setTimeout(function () { btn.innerHTML = original; btn.disabled = false; btn.removeAttribute('aria-busy'); btn.__busy = false; }, 1200);
        return;
      }
      var submitRes = await fetch(PROXY + '/formula/submit', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': uuidv4() },
        body: JSON.stringify({
          stack: { stack_name: tin || 'My Blend', primary_goals: [], ingredients: [] },
          base_slug: slug,
        }),
      });
      if (!submitRes.ok) throw new Error(await readError(submitRes, 'Something went wrong — please try again.'));
      var submitted = await submitRes.json();
      if (!submitted.formula_id) throw new Error('Something went wrong — please try again.');

      // 14 Oct 2026: the submit response already carries the registered
      // bundle; the /pdp round trip is only a fallback now.
      var payload = (submitted.bundle && Array.isArray(submitted.bundle.items) && submitted.bundle.items.length) ? { bundle: submitted.bundle } : null;
      if (!payload) {
        var pdpRes = await fetch(PROXY + '/formula/' + encodeURIComponent(submitted.formula_id) + '/pdp', {
          credentials: 'same-origin', headers: { Accept: 'application/json' },
        });
        if (!pdpRes.ok) throw new Error('Something went wrong preparing your order — please try again.');
        payload = await pdpRes.json();
      }
      if (!payload.bundle || !Array.isArray(payload.bundle.items) || !payload.bundle.items.length) {
        throw new Error('Something went wrong preparing your order — please try again.');
      }
      // 9 Oct 2026: `_yt_formula` on every line — the basket drawer's "Add to
      // your scoop" reads the blend's recipe from it to re-mint.
      var props = Object.assign({}, payload.bundle.properties || {}, { _yt_formula: submitted.formula_id });
      var oneTime = otMode();
      if (oneTime) {
        // Same shape as the base page's one-time add: the plan-less ':ot'
        // bundle key and the test stamp.
        if (props._rc_bundle) props = Object.assign({}, props, { _rc_bundle: props._rc_bundle + ':ot' });
        props = Object.assign({}, props, { _yt_offer: 'ot30' });
      }
      var items = payload.bundle.items.map(function (i) {
        var line = { id: Number(i.variant_id), quantity: i.quantity || 1, properties: props };
        if (!oneTime && i.selling_plan_30d) line.selling_plan = Number(i.selling_plan_30d);
        return line;
      });
      // Shopify prepends new lines — reversed keeps the base first in the basket.
      var cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items.slice().reverse() }),
      });
      if (!cartRes.ok) throw new Error(await readError(cartRes, 'Something went wrong adding your blend — please try again.'));

      // One free spoon per basket, not one per blend — and this path can go
      // straight to checkout without the drawer, which is where the tidy-up
      // otherwise happens.
      if (window.ytDedupeGifts) { try { await window.ytDedupeGifts(); } catch (_e) {} }

      if (window.ytTrack) {
        try { ytTrack('category_quick_add', { base: slug, label: btn.getAttribute('data-yt-track-label') || slug, location: 'category' }); } catch (_e) {}
      }
      btn.textContent = 'Added to basket ✓';
      // data-yt-after="checkout": the landing pages' sticky bar adds the tin
      // and goes straight to checkout (Graham, 7 Oct 2026).
      if (btn.dataset.ytAfter === 'checkout') { window.location.href = '/checkout'; return; }
      if (window.__ytCartDrawer && typeof window.__ytCartDrawer.open === 'function') {
        await window.__ytCartDrawer.open();
      } else {
        window.location.assign('/cart');
        return;
      }
      setTimeout(function () {
        btn.innerHTML = original;
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.__busy = false;
      }, 2500);
    } catch (e) {
      btn.innerHTML = original;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.__busy = false;
      if (errEl) {
        errEl.textContent = e && e.message ? e.message : 'Something went wrong — please try again.';
        errEl.hidden = false;
      }
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-yt-quick-add]');
    if (!btn) return;
    e.preventDefault();
    add(btn);
  });
})();
